import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { billingApi } from '../utils/api';

/**
 * Landing page after Stripe Checkout redirects back.
 *
 * Stripe doesn't send us a webhook synchronously, so we poll our own
 * /billing/subscription endpoint for up to ~8s waiting for the status to
 * flip to ACTIVE / TRIAL. Either way, after the grace period we push the
 * user into the onboarding wizard so they can finish setting up their
 * company while the final webhook reconciles in the background.
 */
export default function SignUpSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<'waiting' | 'ready'>('waiting');

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const resp = await billingApi.getSubscription();
        const s = resp.data?.status;
        if (s === 'ACTIVE' || s === 'TRIAL') {
          if (cancelled) return;
          setStatus('ready');
          await refreshUser();
          navigate('/onboarding', { replace: true });
          return;
        }
      } catch {
        // Ignore transient errors while Stripe finishes up.
      }
      if (!cancelled && attempts < 8) {
        setTimeout(tick, 1000);
      } else if (!cancelled) {
        // Give up waiting and move on — the webhook will reconcile.
        setStatus('ready');
        await refreshUser();
        navigate('/onboarding', { replace: true });
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [navigate, refreshUser, sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white px-6">
      <div className="max-w-md w-full bg-white shadow-sm border border-slate-100 rounded-2xl p-8 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircleIcon className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900">Payment received</h1>
        <p className="text-slate-600 text-sm mt-2">
          Welcome aboard. We're activating your subscription — this only takes a few seconds.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 text-sm">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
          {status === 'waiting' ? 'Waiting for Stripe…' : 'Redirecting to onboarding…'}
        </div>
      </div>
    </div>
  );
}
